import type { FeatureMetadataRow } from './feature-metadata.models';
import type {
  VoyageContributingFactorRow,
  VoyageContributingFactorsVoyage,
  VoyageContributingPerCaptainNamed,
  VoyageContributingPerCaptainScalar,
} from './voyage-contributing-factors.models';

/** Matches Inference API `originalVoyageRiskPrediction`. */
export interface InferenceApiVoyageRiskPrediction {
  VoyageLevelRisk: number;
  VoyageLevelRiskCategory: string | null;
}

/** Matches Inference API `originalShipRiskPrediction`. */
export interface InferenceApiShipRiskPrediction {
  ShipLevelRisk: number;
  ShipLevelRiskCategory: string | null;
}

/** One factor in `originalFactors` (snake_case per API sample). */
export interface InferenceApiFactorPayload {
  feature_id: number;
  feature: string;
  feature_value_ui: InferenceApiFactorValueUi;
  /**
   * Parallel UI snapshot for downstream use (e.g. per-captain dim crew ids from dropdown in `value`
   * while `feature_value_ui` keeps model weights for named captain rows).
   */
  feature_value_ui_temp: InferenceApiFactorValueUi;
  shap_value_ui: InferenceApiShapUi;
  feature_value: number | null;
  apiName: string;
  dbName: string;
}

/** `changedFactors[]` entry: baseline factor snapshot + `changedValue` (updated UI / model input only). */
export interface InferenceApiChangedFactorPayload {
  feature_id: number;
  feature: string;
  feature_value_ui: InferenceApiFactorValueUi;
  shap_value_ui: InferenceApiShapUi;
  feature_value: number | null;
  apiName: string;
  dbName: string;
  /** Value after the user edit (scalar, text, or per-captain map matching the factor shape). */
  changedValue: InferenceApiChangedValue;
}

/** Payload for `changedValue` (mirrors editable side of `feature_value_ui`). */
export type InferenceApiChangedValue =
  | number
  | string
  | null
  | Record<string, number>
  | Record<string, InferenceApiNamedCaptainValueUi>;

export type InferenceApiShapUi = number | Record<string, number> | null;

export type InferenceApiFactorValueUi =
  | number
  | string
  | Record<string, number>
  | Record<string, InferenceApiNamedCaptainValueUi>;

export interface InferenceApiNamedCaptainValueUi {
  name: string;
  value: number;
}

export interface InferenceApiVoyagePayload {
  DimVoyageId: string;
  VoyageNo: string;
  captainWeightDict: Readonly<Record<string, number>>;
  captainCrewIdDict: Readonly<Record<string, number>>;
  baseValue: number;
  originalVoyageRiskPrediction: InferenceApiVoyageRiskPrediction;
  originalFactors: InferenceApiFactorPayload[];
  changedFactors: InferenceApiChangedFactorPayload[];
}

export interface InferenceApiShipDetailsPayload {
  DimShipId: string;
  Fleet: string;
  originalShipRiskPrediction: InferenceApiShipRiskPrediction;
}

export interface InferenceApiRequestBody {
  voyages: InferenceApiVoyagePayload[];
  shipDetails: InferenceApiShipDetailsPayload;
}

export interface InferenceApiShipDetailsInput {
  dimShipId: string | number;
  fleet: string;
  shipLevelRisk: number;
  shipLevelRiskCategory: string | null;
}

function toFiniteNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function indexMetadataByFeatureId(
  rows: readonly FeatureMetadataRow[],
): ReadonlyMap<number, FeatureMetadataRow> {
  return new Map(rows.map((r) => [r.featureId, r]));
}

function collectFactorRows(voyage: VoyageContributingFactorsVoyage): VoyageContributingFactorRow[] {
  const { factors } = voyage;
  if (!factors) {
    return [];
  }
  return [
    ...(factors.captain ?? []),
    ...(factors.crew ?? []),
    ...(factors.ship ?? []),
    ...(factors.voyage ?? []),
  ];
}

function scalarFeatureValueUi(row: VoyageContributingFactorRow): number | string {
  if (row.scalarValueUi != null && String(row.scalarValueUi).trim() !== '') {
    const n = Number(String(row.scalarValueUi).replace(/,/g, ''));
    if (Number.isFinite(n)) {
      return n;
    }
    return String(row.scalarValueUi);
  }
  const a = row.aggregateValue;
  if (typeof a === 'number' && Number.isFinite(a)) {
    return a;
  }
  return 0;
}

function scalarShapUi(row: VoyageContributingFactorRow): number {
  const s = toFiniteNumber(row.aggregateShap);
  if (s !== null) {
    return s;
  }
  const t = toFiniteNumber(row.shap);
  return t ?? 0;
}

function isPerCaptainNamedEntry(
  entry: VoyageContributingPerCaptainScalar | VoyageContributingPerCaptainNamed,
): entry is VoyageContributingPerCaptainNamed {
  return (
    entry != null &&
    typeof entry === 'object' &&
    'name' in entry &&
    typeof (entry as VoyageContributingPerCaptainNamed).name === 'string'
  );
}

function isEmptyPlainObject(value: unknown): boolean {
  return (
    value !== null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.keys(value as object).length === 0
  );
}

/** Per-captain factors with no roster data should send `feature_value: null`, not `0`. */
function resolveInferenceFeatureValue(
  modelValue: number,
  feature_value_ui: InferenceApiFactorValueUi,
  feature_value_ui_temp: InferenceApiFactorValueUi,
  shap_value_ui: InferenceApiShapUi,
): number | null {
  if (
    isEmptyPlainObject(feature_value_ui) &&
    isEmptyPlainObject(feature_value_ui_temp) &&
    isEmptyPlainObject(shap_value_ui)
  ) {
    return null;
  }
  return modelValue;
}

export function contributingFactorRowToInferencePayload(
  row: VoyageContributingFactorRow,
  meta: FeatureMetadataRow | undefined,
): InferenceApiFactorPayload {
  const apiName = row.apiName;
  const dbName = meta?.dbName ?? '';
  const featureLabel = apiName;
  const featureValueModel = toFiniteNumber(row.aggregateValue) ?? 0;

  if (row.shape === 'perCaptainNamed') {
    const feature_value_ui: Record<string, InferenceApiNamedCaptainValueUi> = {};
    const feature_value_ui_temp: any = {};
    const shap_value_ui_map: Record<string, number> = {};
    if (row.perCaptain) {
      for (const [captainId, entry] of Object.entries(row.perCaptain)) {
        if (!entry || typeof entry !== 'object') {
          continue;
        }
        if (isPerCaptainNamedEntry(entry)) {
          feature_value_ui[captainId] = {
            name: entry.name,
            value: toFiniteNumber(entry.value) ?? 0,
          };
          const crewId =
            entry.uiSelectedDimCrewId != null
              ? toFiniteNumber(entry.uiSelectedDimCrewId)
              : toFiniteNumber(entry.name);
          // Always write to temp when uiSelectedDimCrewId was explicitly set by a user
          // dropdown selection (covers the no-captain-assigned case where the perCaptain
          // key IS the DimCrewId, so captainId === String(crewId) but we still need
          // changedValue to carry the selection). When falling back to entry.name,
          // skip only if key and numeric name are identical (truly no change).
          if (crewId !== null && (entry.uiSelectedDimCrewId != null || captainId !== String(crewId))) {
            feature_value_ui_temp[captainId] = crewId;
          }
          shap_value_ui_map[captainId] = toFiniteNumber(entry.shap) ?? 0;
        }
      }
    }
    // When there is no per-captain data (e.g. no-captain-assigned voyage) emit null for
    // shap_value_ui to match the backend's expected format instead of an empty dict.
    const shap_value_ui: InferenceApiShapUi =
      Object.keys(shap_value_ui_map).length > 0 ? shap_value_ui_map : null;
    return {
      feature_id: row.featureId,
      feature: featureLabel,
      feature_value_ui,
      feature_value_ui_temp,
      shap_value_ui,
      feature_value: resolveInferenceFeatureValue(
        featureValueModel,
        feature_value_ui,
        feature_value_ui_temp,
        shap_value_ui_map,
      ),
      apiName,
      dbName,
    };
  }

  if (row.perCaptain && typeof row.perCaptain === 'object' && Object.keys(row.perCaptain).length > 0) {
    const feature_value_ui: Record<string, number> = {};
    const shap_value_ui: Record<string, number> = {};
    for (const [captainId, entry] of Object.entries(row.perCaptain)) {
      if (!entry || typeof entry !== 'object') {
        continue;
      }
      const esc = entry as VoyageContributingPerCaptainScalar;
      feature_value_ui[captainId] = toFiniteNumber(esc.value) ?? 0;
      shap_value_ui[captainId] = toFiniteNumber(esc.shap) ?? 0;
    }
    const feature_value_ui_temp = { ...feature_value_ui };
    return {
      feature_id: row.featureId,
      feature: featureLabel,
      feature_value_ui,
      feature_value_ui_temp,
      shap_value_ui,
      feature_value: resolveInferenceFeatureValue(
        featureValueModel,
        feature_value_ui,
        feature_value_ui_temp,
        shap_value_ui,
      ),
      apiName,
      dbName,
    };
  }

  const feature_value_ui = scalarFeatureValueUi(row);
  const feature_value_ui_temp = feature_value_ui;
  const shap_value_ui = scalarShapUi(row);
  return {
    feature_id: row.featureId,
    feature: featureLabel,
    feature_value_ui,
    feature_value_ui_temp,
    shap_value_ui,
    feature_value: resolveInferenceFeatureValue(
      featureValueModel,
      feature_value_ui,
      feature_value_ui_temp,
      shap_value_ui,
    ),
    apiName,
    dbName,
  };
}

function sortFactorsByFeatureId(factors: InferenceApiFactorPayload[]): InferenceApiFactorPayload[] {
  return [...factors].sort((a, b) => a.feature_id - b.feature_id);
}

/** All factor rows for one voyage in Inference API factor shape (sorted by `feature_id`). */
export function serializeVoyageFactorsToInferencePayloads(
  voyage: VoyageContributingFactorsVoyage,
  metaByFeatureId: ReadonlyMap<number, FeatureMetadataRow>,
): InferenceApiFactorPayload[] {
  const rows = collectFactorRows(voyage);
  return sortFactorsByFeatureId(
    rows.map((r) => contributingFactorRowToInferencePayload(r, metaByFeatureId.get(r.featureId))),
  );
}

function deepEqualInferenceCurrentFields(a: unknown, b: unknown): boolean {
  if (a === b) {
    return true;
  }
  if (typeof a === 'number' && typeof b === 'number' && Number.isFinite(a) && Number.isFinite(b)) {
    return Math.abs(a - b) < 1e-9;
  }
  if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') {
    return false;
  }
  if (Array.isArray(a) !== Array.isArray(b)) {
    return false;
  }
  if (Array.isArray(a)) {
    const bb = b as unknown[];
    if (a.length !== bb.length) {
      return false;
    }
    return a.every((x, i) => deepEqualInferenceCurrentFields(x, bb[i]));
  }
  const ao = a as Record<string, unknown>;
  const bo = b as Record<string, unknown>;
  const keysA = Object.keys(ao).sort();
  const keysB = Object.keys(bo).sort();
  if (keysA.length !== keysB.length || !keysA.every((k, i) => k === keysB[i])) {
    return false;
  }
  return keysA.every((k) => deepEqualInferenceCurrentFields(ao[k], bo[k]));
}

/** True when editable model/UI inputs match; ignores `shap_value_ui` and identity strings. */
function inferenceFactorCurrentFieldsEqual(a: InferenceApiFactorPayload, b: InferenceApiFactorPayload): boolean {
  return (
    deepEqualInferenceCurrentFields(a.feature_value, b.feature_value) &&
    deepEqualInferenceCurrentFields(a.feature_value_ui, b.feature_value_ui) &&
    deepEqualInferenceCurrentFields(a.feature_value_ui_temp, b.feature_value_ui_temp)
  );
}

function sortChangedFactorsByFeatureId(
  factors: InferenceApiChangedFactorPayload[],
): InferenceApiChangedFactorPayload[] {
  return [...factors].sort((a, b) => a.feature_id - b.feature_id);
}

/** Prefer UI-facing value for `changedValue`; fall back to model `feature_value`. */
function extractInferenceChangedValue(
  cur: InferenceApiFactorPayload,
  apiName: string,
  onlyCaptainId?: string | null,
): InferenceApiChangedValue {
  const ui = (apiName == "CaptainCrew_AccidentPerVoyageRatio_weighted") ? cur.feature_value_ui_temp : cur.feature_value_ui;
  if (typeof ui === 'number' || typeof ui === 'string') {
    return ui;
  }
  if (ui !== null && typeof ui === 'object' && !Array.isArray(ui)) {
    if (onlyCaptainId != null && onlyCaptainId !== '') {
      const dict = ui as Record<string, number | InferenceApiNamedCaptainValueUi>;
      if (Object.prototype.hasOwnProperty.call(dict, onlyCaptainId)) {
        return { [onlyCaptainId]: dict[onlyCaptainId] } as InferenceApiChangedValue;
      }
    }
    return ui as InferenceApiChangedValue;
  }
  return cur.feature_value;
}

function pickCaptainScopedRecordUi<T extends InferenceApiFactorValueUi | InferenceApiShapUi>(
  ui: T,
  onlyCaptainId?: string | null,
): T {
  if (onlyCaptainId == null || onlyCaptainId === '') {
    return ui;
  }
  if (ui !== null && typeof ui === 'object' && !Array.isArray(ui)) {
    const dict = ui as Record<string, unknown>;
    if (Object.prototype.hasOwnProperty.call(dict, onlyCaptainId)) {
      return { [onlyCaptainId]: dict[onlyCaptainId] } as T;
    }
  }
  return ui;
}

function pickCaptainScopedFeatureValueUi(
  ui: InferenceApiFactorValueUi,
  onlyCaptainId?: string | null,
): InferenceApiFactorValueUi {
  return pickCaptainScopedRecordUi(ui, onlyCaptainId);
}

function pickCaptainScopedShapUi(
  ui: InferenceApiShapUi,
  onlyCaptainId?: string | null,
): InferenceApiShapUi {
  return pickCaptainScopedRecordUi(ui, onlyCaptainId);
}

/**
 * Builds `changedFactors` entries: reference `feature_value_ui` / `feature_value` / SHAP (last server
 * state before this edit) plus `changedValue` from the merged (edited) row when inputs differ.
 */
export function buildInferenceChangedFactorPayloads(
  referenceFactors: readonly InferenceApiFactorPayload[],
  currentFactors: readonly InferenceApiFactorPayload[],
  factor: any,
  onlyCaptainId?: string | null,
): InferenceApiChangedFactorPayload[] {
  const refById = new Map<number, InferenceApiFactorPayload>(
    referenceFactors.map((f) => [f.feature_id, f]),
  );
  const changed: InferenceApiChangedFactorPayload[] = [];
  const factorData = currentFactors.filter((f) => f.feature_id === factor.featureId);
  for (const cur of factorData) {
    const ref = refById.get(cur.feature_id);
    if (ref === undefined) {
      continue;
    }
    if (inferenceFactorCurrentFieldsEqual(ref, cur)) {
      continue;
    }
    changed.push({
      feature_id: ref.feature_id,
      feature: ref.feature,
      feature_value_ui: pickCaptainScopedFeatureValueUi(ref.feature_value_ui, onlyCaptainId),
      shap_value_ui: pickCaptainScopedShapUi(ref.shap_value_ui, onlyCaptainId),
      feature_value: ref.feature_value,
      apiName: ref.apiName,
      dbName: ref.dbName,
      changedValue: extractInferenceChangedValue(cur, ref.apiName, onlyCaptainId),
    });
  }
  return sortChangedFactorsByFeatureId(changed);
}

function voyageShellPayload(
  voyage: VoyageContributingFactorsVoyage,
  originalFactors: InferenceApiFactorPayload[],
  changedFactors: readonly InferenceApiChangedFactorPayload[],
): InferenceApiVoyagePayload {
  const weights = voyage.captainWeightDict ?? {};
  const captainWeightDict: Record<string, number> = {};
  for (const [k, v] of Object.entries(weights)) {
    const n = toFiniteNumber(v);
    if (n !== null) {
      captainWeightDict[k] = n;
    }
  }

  const crewIds = voyage.captainCrewIdDict ?? {};
  const captainCrewIdDict: Record<string, number> = {};
  for (const [k, v] of Object.entries(crewIds)) {
    const n = toFiniteNumber(v);
    if (n !== null) {
      captainCrewIdDict[k] = n;
    }
  }

  return {
    DimVoyageId: String(voyage.voyageId),
    VoyageNo: String(voyage.voyageNo ?? ''),
    captainWeightDict,
    captainCrewIdDict,
    baseValue: toFiniteNumber(voyage.baseValue) ?? 0,
    originalVoyageRiskPrediction: {
      VoyageLevelRisk: toFiniteNumber(voyage.voyageLevelRisk) ?? 0,
      VoyageLevelRiskCategory: voyage.voyageLevelRiskCategory,
    },
    originalFactors,
    changedFactors: [...changedFactors],
  };
}

export function voyageContributingFactorsToInferenceVoyage(
  voyage: VoyageContributingFactorsVoyage,
  metaByFeatureId: ReadonlyMap<number, FeatureMetadataRow>,
  changedFactors: readonly InferenceApiChangedFactorPayload[] = [],
): InferenceApiVoyagePayload {
  const originalFactors = serializeVoyageFactorsToInferencePayloads(voyage, metaByFeatureId);
  return voyageShellPayload(voyage, originalFactors, changedFactors);
}

function toShipDetailsPayload(input: InferenceApiShipDetailsInput): InferenceApiShipDetailsPayload {
  return {
    DimShipId: String(input.dimShipId),
    Fleet: input.fleet,
    originalShipRiskPrediction: {
      ShipLevelRisk: input.shipLevelRisk,
      ShipLevelRiskCategory: input.shipLevelRiskCategory,
    },
  };
}

export interface BuildInferenceApiRequestParams {
  voyages: readonly VoyageContributingFactorsVoyage[];
  factor: any;
  featureMetadata: readonly FeatureMetadataRow[];
  shipDetails: InferenceApiShipDetailsInput;
  /**
   * Frozen voyages from the initial GET — fallback when `referenceVoyagesById` is absent.
   * UI “Current:” labels still use this; inference payloads prefer `referenceVoyagesById`.
   */
  baselineVoyagesById?: ReadonlyMap<string, VoyageContributingFactorsVoyage>;
  /**
   * Contributing-factors snapshot before the current form overlay (last GET + recalculation merges).
   * Drives `originalFactors` and the reference side of `changedFactors` (not the first-load GET).
   */
  referenceVoyagesById?: ReadonlyMap<string, VoyageContributingFactorsVoyage>;
  /**
   * When set, `changedFactors` is computed only for this voyage id; every other voyage
   * gets `changedFactors: []` while still including full voyage shells in the payload.
   */
  changedFactorsOnlyForVoyageId?: string | number | null;
  /**
   * When set, per-captain `changedValue` dicts in `changedFactors` are narrowed to
   * only this captain id (the captain whose UI input was edited). Non-per-captain
   * factors are unaffected. Ignored when null/empty.
   */
  changedFactorsOnlyForCaptainId?: string | number | null;
}

export function buildInferenceApiRequestBody(params: BuildInferenceApiRequestParams): InferenceApiRequestBody {
  const meta = indexMetadataByFeatureId(params.featureMetadata);
  const baselineMap = params.baselineVoyagesById;
  const factor = params.factor;
  const restrictChanged =
    params.changedFactorsOnlyForVoyageId != null &&
    String(params.changedFactorsOnlyForVoyageId).length > 0;
  const onlyCaptainId =
    params.changedFactorsOnlyForCaptainId != null &&
    String(params.changedFactorsOnlyForCaptainId).length > 0
      ? String(params.changedFactorsOnlyForCaptainId)
      : null;

  const referenceMap = params.referenceVoyagesById;

  const voyages = params.voyages.map((merged) => {
    const id = String(merged.voyageId);
    const baseline = baselineMap?.get(id);
    const reference = referenceMap?.get(id) ?? baseline;
    const originalSource = reference ?? merged;
    const originalFactors = serializeVoyageFactorsToInferencePayloads(originalSource, meta);

    if (restrictChanged && String(params.changedFactorsOnlyForVoyageId) !== id) {
      return voyageShellPayload(merged, originalFactors, []);
    }

    if (baseline != null || reference != null) {
      const referenceFactors = serializeVoyageFactorsToInferencePayloads(
        reference ?? merged,
        meta,
      );
      const currentFactors = serializeVoyageFactorsToInferencePayloads(merged, meta);
      const changedFactors = buildInferenceChangedFactorPayloads(
        referenceFactors,
        currentFactors,
        factor,
        onlyCaptainId,
      );
      return voyageShellPayload(merged, originalFactors, changedFactors);
    }
    return voyageShellPayload(merged, originalFactors, []);
  });

  return {
    voyages,
    shipDetails: toShipDetailsPayload(params.shipDetails),
  };
}
